import type { Ref } from 'react';

import type { TeleprompterSpeed } from './videoRecorder.utils';
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import { Arrows, General, Images, Media, Shapes } from '../StrokeIcons';
import { formatClock } from './videoRecorder.utils';

export type VideoRecorderPhase =
  | 'idle' // camera not opened yet
  | 'starting' // waiting on the camera permission prompt
  | 'ready' // live preview, not recording
  | 'countdown'
  | 'recording'
  | 'paused'
  | 'preview' // recorded, playing back before sending
  | 'sending'
  | 'denied' // camera/mic permission refused
  | 'no_camera' // no usable camera on this device
  | 'error';

export type VideoRecorderPureProps = {
  phase: VideoRecorderPhase;
  /** What the rep reads — scrolls on the teleprompter while recording. */
  script: string;
  speed: TeleprompterSpeed;
  mirrored: boolean;
  /** Seconds left in the pre-roll countdown. */
  countdown: number;
  elapsedMs: number;
  maxMs: number;
  /** 0 → 1: how far the teleprompter has scrolled (driven by recording time, so pausing pauses it). */
  teleprompterProgress: number;
  /** Object URL of the finished recording, for the preview player. */
  previewUrl: string | null;
  errorMessage?: string | null;
  /** Attach the live camera stream to the preview <video>. */
  liveVideoRef?: Ref<HTMLVideoElement>;
  onOpenCamera: () => void;
  onRecord: () => void;
  onStop: () => void;
  onPauseToggle: () => void;
  onRetake: () => void;
  onSend: () => void;
  onCancel: () => void;
  onSpeedChange: (speed: TeleprompterSpeed) => void;
  onMirrorToggle: () => void;
  className?: string;
};

const SPEEDS: { value: TeleprompterSpeed; label: string }[] = [
  { value: 'slow', label: 'Slow' },
  { value: 'normal', label: 'Normal' },
  { value: 'fast', label: 'Fast' },
];

/**
 * The video-note recorder, data-free: a 9:16 camera frame with the script scrolling on a teleprompter
 * band near the top (so the rep's eyes stay close to the lens), a countdown, a timer against the cap,
 * and a preview to watch before sending. All state comes in as props — see `VideoRecorder` for the
 * MediaRecorder / camera wiring.
 */
export function VideoRecorderPure({
  phase,
  script,
  speed,
  mirrored,
  countdown,
  elapsedMs,
  maxMs,
  teleprompterProgress,
  previewUrl,
  errorMessage,
  liveVideoRef,
  onOpenCamera,
  onRecord,
  onStop,
  onPauseToggle,
  onRetake,
  onSend,
  onCancel,
  onSpeedChange,
  onMirrorToggle,
  className,
}: VideoRecorderPureProps) {
  const live = phase === 'ready' || phase === 'countdown' || phase === 'recording' || phase === 'paused';
  const capturing = phase === 'recording' || phase === 'paused';
  const showTeleprompter = script.trim().length > 0 && (live || phase === 'starting');
  const progress = Math.min(1, Math.max(0, teleprompterProgress));

  if (phase === 'denied' || phase === 'no_camera' || phase === 'error') {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-center dark:border-red-800 dark:bg-red-950/40',
          className,
        )}
        data-testid={`video-recorder-${phase}`}>
        <Images.Camera01 className="size-6 text-red-500" />
        <p className="text-sm text-red-700 dark:text-red-300">
          {phase === 'denied'
            ? 'Camera or microphone access was blocked. Allow both for this site in your browser, then try again.'
            : phase === 'no_camera'
              ? 'No camera was found (or another app is using it). Connect a camera, close other video apps, then try again.'
              : (errorMessage ?? 'Something went wrong with the camera.')}
        </p>
        <div className="flex gap-2">
          <Button size="sm" color="red" onClick={onOpenCamera} data-testid="video-retry-camera">
            Try again
          </Button>
          <Button size="sm" color="gray" tone="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center gap-3', className)} data-testid="video-recorder">
      {/* The 9:16 frame — the same shape the recipient sees on their phone. */}
      <div
        className="relative aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-xl bg-stone-900"
        data-testid="video-frame">
        {phase === 'preview' || phase === 'sending' ? (
          previewUrl != null && (
            <video
              src={previewUrl}
              controls
              playsInline
              className="size-full object-cover"
              data-testid="video-preview-player"
            />
          )
        ) : (
          <video
            ref={liveVideoRef}
            autoPlay
            muted
            playsInline
            className={cn('size-full object-cover', mirrored && '-scale-x-100')}
            data-testid="video-live"
          />
        )}

        {phase === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <Images.Camera01 className="size-8 text-white/70" />
            <p className="text-sm text-white/80">Read the script on camera, then send it as a LinkedIn video.</p>
            <Button size="sm" color="red" onClick={onOpenCamera} data-testid="video-open-camera">
              Turn on camera
            </Button>
          </div>
        )}
        {phase === 'starting' && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80">
            Waiting for the camera…
          </div>
        )}

        {/* Teleprompter: a band near the top, close to the lens, scrolled by recording time. */}
        {showTeleprompter && (
          <div
            className="absolute inset-x-0 top-0 h-[38%] overflow-hidden bg-black/55 px-3 pt-2"
            data-testid="video-teleprompter">
            <div
              className="text-[19px] font-medium leading-snug text-white"
              style={{ transform: `translateY(calc(${-progress * 100}% + ${progress === 0 ? 0 : 24}px))` }}
              data-testid="video-teleprompter-text"
              data-progress={progress.toFixed(3)}>
              {script}
            </div>
          </div>
        )}

        {phase === 'countdown' && (
          <div
            className="absolute inset-0 flex items-center justify-center text-7xl font-bold text-white drop-shadow"
            data-testid="video-countdown">
            {countdown}
          </div>
        )}

        {capturing && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white tabular-nums">
            <span className={cn('size-2 rounded-full bg-red-500', phase === 'recording' && 'animate-pulse')} />
            <span data-testid="video-timer">
              {formatClock(elapsedMs)} / {formatClock(maxMs)}
            </span>
          </div>
        )}
      </div>

      {/* Teleprompter + framing controls, before and during the take. */}
      {live && (
        <div className="flex w-full max-w-[280px] items-center gap-2">
          <ButtonGroup size="xs" shape="pill" color="gray" tone="outline" equalWidth className="flex-1">
            {SPEEDS.map((s) => (
              <Button
                key={s.value}
                active={speed === s.value}
                onClick={() => onSpeedChange(s.value)}
                tooltip={`Teleprompter speed: ${s.label.toLowerCase()}`}
                data-testid={`video-speed-${s.value}`}>
                {s.label}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            size="xs"
            color="gray"
            tone="outline"
            active={mirrored}
            onClick={onMirrorToggle}
            tooltip={mirrored ? 'Showing a mirror image (the sent video is not mirrored)' : 'Show as a mirror'}
            data-testid="video-mirror"
            icon={<Arrows.FlipBackward className="size-4" />}
          />
        </div>
      )}

      <div className="flex w-full max-w-[280px] gap-2">
        {phase === 'ready' && (
          <>
            <Button size="sm" color="gray" tone="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              color="red"
              className="flex-1"
              onClick={onRecord}
              data-testid="video-record"
              icon={<Media.Recording01 />}>
              Record
            </Button>
          </>
        )}
        {phase === 'countdown' && (
          <Button size="sm" color="gray" tone="outline" className="flex-1" onClick={onStop}>
            Cancel countdown
          </Button>
        )}
        {capturing && (
          <>
            <Button
              size="sm"
              color="gray"
              tone="outline"
              onClick={onPauseToggle}
              data-testid="video-pause"
              icon={phase === 'paused' ? <Media.Play /> : <Media.PauseCircle />}>
              {phase === 'paused' ? 'Resume' : 'Pause'}
            </Button>
            <Button
              size="sm"
              color="red"
              className="flex-1"
              onClick={onStop}
              data-testid="video-stop"
              icon={<Shapes.Square />}>
              Stop
            </Button>
          </>
        )}
        {(phase === 'preview' || phase === 'sending') && (
          <>
            <Button
              size="sm"
              color="gray"
              tone="outline"
              disabled={phase === 'sending'}
              onClick={onRetake}
              data-testid="video-retake">
              Retake
            </Button>
            <Button
              size="sm"
              color="green"
              className="flex-1"
              disabled={phase === 'sending'}
              onClick={onSend}
              data-testid="video-send"
              icon={<General.Check />}>
              {phase === 'sending' ? 'Sending…' : 'Send video'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
